import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ListChecks, Plus, X, Loader2, ExternalLink, Route, MessageSquareQuote } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Section, Field, AutoText, ListEditor, Segmented } from './OnsiteFields';
import { uid, ago } from './useOnsitePlan';

export function ChecklistSection({ plan, list, setField, check, run, id, icon, title, subtitle, children }) {
  const items = plan[list] || [];
  const save = (v) => setField(list, v, list);
  const [busy, setBusy] = useState(false);
  const open = items.filter((x) => !x.done && !x.task_id).length;
  const toTasks = async () => {
    setBusy(true);
    const r = await run(() => api.onsiteTasks(plan.id, list));
    if (r) { toast.success(`${r.created} Tasks angelegt — sichtbar unter Workflow / Meine Tasks`); run(() => api.onsiteGet(plan.id)); }
    setBusy(false);
  };
  const done = items.filter((x) => x.done).length;
  return (
    <Section id={id} icon={icon || ClipboardCheck} title={title} subtitle={subtitle} meta={plan.section_meta?.[list]} data={items} testId={`onsite-${list}`}
      right={<>
        <span className="text-[10px] font-mono text-muted-foreground">{done}/{items.length}</span>
        <button onClick={toTasks} disabled={busy || open === 0} data-testid={`onsite-${list}-tasks-btn`} title="Offene Punkte als Deal-Tasks anlegen (an dich, fällig am Termin)"
          className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300 border border-sky-900/40 rounded-sm px-2 py-1 disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />} {open} offene → Tasks
        </button>
      </>}>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={it.id} className="flex items-start gap-2 group" data-testid={`onsite-${list}-item-${i}`}>
            <input type="checkbox" checked={!!it.done} onChange={(e) => check(list, it.id, e.target.checked)} data-testid={`onsite-${list}-check-${i}`}
              className="mt-1.5 accent-teal-500 w-3.5 h-3.5 shrink-0 cursor-pointer" />
            <div className="flex-1 min-w-0">
              <AutoText value={it.text} rows={1} onChange={(v) => save(items.map((x) => (x.id === it.id ? { ...x, text: v } : x)))} className={it.done ? 'line-through text-muted-foreground' : ''} />
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70 px-1">
                {it.done && it.done_by && <span>✓ {it.done_by} · {ago(it.done_at)}</span>}
                {it.task_id && <Link to={`/projects/${plan.project_id}#section-workflow`} className="inline-flex items-center gap-0.5 text-sky-400/80 hover:text-sky-300"><ExternalLink className="w-2.5 h-2.5" /> Task</Link>}
              </div>
            </div>
            <button onClick={() => save(items.filter((x) => x.id !== it.id))} className="mt-1.5 text-muted-foreground/30 hover:text-red-400 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={() => save([...items, { id: uid(), text: '', done: false, done_by: '', done_at: '' }])} data-testid={`onsite-${list}-add`}
          className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300"><Plus className="w-3 h-3" /> Punkt</button>
      </div>
      {children}
    </Section>
  );
}

export function ProcessOutlookSection({ plan, setField }) {
  const steps = plan.process || [];
  const upd = (i, patch) => setField('process', steps.map((s, j) => (j === i ? { ...s, ...patch } : s)), 'process');
  return (
    <Section id="os-process" icon={Route} title="Wie es weitergeht — 5 Schritte" subtitle="intern: mit Zeitangaben; extern nur Größenordnungen" meta={plan.section_meta?.process} data={steps} testId="onsite-process">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {steps.map((s, i) => (
          <div key={i} className="border border-border/70 rounded-sm p-3 space-y-2 bg-secondary/10">
            <p className="text-xs font-medium text-foreground">{s.title}</p>
            <ListEditor items={s.points || []} onChange={(v) => upd(i, { points: v })} testId={`onsite-process-${i}`} />
            <Field label="Offen / intern"><AutoText value={s.open} rows={1} onChange={(v) => upd(i, { open: v })} /></Field>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border/50">
        <Field label="So sagen wir es am Ende (Skript)" className="md:col-span-2"><AutoText value={plan.closing_script} onChange={(v) => setField('closing_script', v, 'process')} /></Field>
        <Field label="Nicht versprechen"><AutoText value={plan.dont_promise} onChange={(v) => setField('dont_promise', v, 'process')} /></Field>
      </div>
    </Section>
  );
}

const TENDENCY = [{ value: 'open', label: 'offen' }, { value: 'go', label: 'Go', cls: 'bg-emerald-600 text-white' }, { value: 'pause', label: 'Pause', cls: 'bg-amber-600 text-white' }, { value: 'no_go', label: 'No-Go', cls: 'bg-red-700 text-white' }];

export function DebriefSection({ plan, setField }) {
  const d = plan.debrief || {};
  return (
    <Section id="os-debrief" icon={MessageSquareQuote} iconClass="text-violet-400" title="Debrief nach dem Termin" meta={plan.section_meta?.debrief} data={d} testId="onsite-debrief">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tendenz</span>
        <Segmented value={d.tendency || 'open'} options={TENDENCY} onChange={(v) => setField('debrief.tendency', v, 'debrief')} testId="onsite-debrief-tendency" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Debrief-Notizen (entlang der drei Ziele)"><AutoText value={d.notes} rows={4} onChange={(v) => setField('debrief.notes', v, 'debrief')} testId="onsite-debrief-notes" /></Field>
        <Field label="Lessons Learned fürs nächste On-Site"><AutoText value={d.lessons} rows={4} onChange={(v) => setField('debrief.lessons', v, 'debrief')} testId="onsite-debrief-lessons" /></Field>
      </div>
    </Section>
  );
}
